import React from 'react';
import { PackageItem, Operator } from '../../types';
import { useLanguage } from '../../context/LanguageContext';
import { Wifi, Clock, ShieldAlert, ChevronRight } from 'lucide-react';
import { OperatorLogo } from '../common/BrandLogo';

interface PackageCardProps {
  pkg: PackageItem;
  operator?: Operator;
  onBuy: (pkg: PackageItem) => void;
}

export const PackageCard: React.FC<PackageCardProps> = ({ pkg, operator, onBuy }) => {
  const { t } = useLanguage();
  const isClosed = operator && operator.package_status !== 'open';

  return (
    <div
      role="button"
      tabIndex={isClosed ? -1 : 0}
      onClick={() => {
        if (!isClosed) onBuy(pkg);
      }}
      onKeyDown={(e) => {
        if (!isClosed && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onBuy(pkg);
        }
      }}
      className={`group bg-surface-card rounded-2xl border border-plum-border p-3.5 sm:p-4 shadow-xs transition-all mb-2.5 relative flex items-center justify-between touch-target ${
        isClosed
          ? 'opacity-70 cursor-not-allowed'
          : 'hover:border-primary/50 hover:shadow-sm cursor-pointer active:scale-[0.99]'
      }`}
    >
      {/* Left content: Logo + Title + Badges */}
      <div className="flex items-center space-x-3 min-w-0 flex-1 mr-3">
        <OperatorLogo
          id={pkg.operator_id}
          size="md"
          className="rounded-xl shadow-xs shrink-0 border border-plum-border/40"
        />
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-plum-dark text-sm sm:text-base tracking-tight leading-snug truncate group-hover:text-primary transition-colors">
            {pkg.name}
          </h3>
          <div className="flex items-center space-x-2 text-xs text-plum-muted mt-1 flex-wrap gap-y-1">
            <span className="inline-flex items-center font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md text-[11px]">
              <Wifi className="w-3 h-3 mr-1" />
              <span>{pkg.data_allowance}</span>
            </span>
            <span className="inline-flex items-center font-medium text-plum-muted bg-surface-page px-2 py-0.5 rounded-md border border-plum-border/40 text-[11px]">
              <Clock className="w-3 h-3 mr-1" />
              <span>{pkg.validity_days} ရက်</span>
            </span>
          </div>
          {pkg.eligibility && (
            <p className="text-[10px] text-plum-muted mt-1 truncate">
              {pkg.eligibility}
            </p>
          )}
        </div>
      </div>

      {/* Right content: Price + Compact Action */}
      <div className="flex flex-col items-end justify-center shrink-0 space-y-1.5">
        <div className="text-right">
          <span className="text-base sm:text-lg font-bold text-primary tracking-tight font-mono">
            {pkg.selling_price.toLocaleString()}
          </span>
          <span className="text-[11px] text-plum-muted ml-1 font-medium">Ks</span>
        </div>

        {isClosed ? (
          <span className="inline-flex items-center text-[10px] font-semibold text-amber-400 bg-amber-500/15 px-2 py-0.5 rounded-lg border border-amber-500/30">
            <ShieldAlert className="w-3 h-3 mr-1 shrink-0" />
            <span>{t.closed}</span>
          </span>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onBuy(pkg);
            }}
            className="bg-primary hover:bg-primary-hover active:scale-95 text-white font-semibold px-3 py-1 rounded-xl text-xs transition shadow-xs flex items-center space-x-1 group-hover:bg-primary-hover"
          >
            <span>{t.buyNow}</span>
            <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
          </button>
        )}
      </div>
    </div>
  );
};
